"use client";

import * as React from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { JURISDICTION_LIST, type Jurisdiction } from "@/lib/simulator/tax";

/* ──────────────────────────────────────────────────────────────────────────
 * JurisdictionSelect — province / territory dropdown for tax calculations.
 *
 * All 13 Canadian jurisdictions (10 provinces + 3 territories) appear in
 * the dropdown. Only those with `supported: true` in the JURISDICTIONS
 * catalog have implemented bracket data; the rest fall back to BC rates
 * with an inline disclosure shown by the parent ("Coming soon — using BC
 * rates as an estimate for now").
 *
 * Having every jurisdiction in the dropdown is a deliberate trust signal:
 * acknowledging that the product is for all Canadians, even if only one
 * jurisdiction is fully implemented today.
 * ────────────────────────────────────────────────────────────────────────── */

export interface JurisdictionSelectProps {
  value: Jurisdiction;
  onChange: (jurisdiction: Jurisdiction) => void;
  disabled?: boolean;
}

export function JurisdictionSelect({ value, onChange, disabled }: JurisdictionSelectProps) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as Jurisdiction)} disabled={disabled}>
      <SelectTrigger
        aria-label="Province or territory"
        className="font-display h-9 w-full text-lg font-medium"
      >
        <SelectValue placeholder="Pick your province" />
      </SelectTrigger>
      <SelectContent>
        {JURISDICTION_LIST.map((j) => (
          <SelectItem key={j.code} value={j.code}>
            <span className="flex items-center gap-2">
              <span>{j.name}</span>
              {j.supported ? null : (
                <span className="text-muted-foreground text-xs">(coming soon)</span>
              )}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
